<template>
  <a-modal
    :visible="visible"
    :destroy-on-close="true"
    title="Edit Invoice"
    ok-text="Update"
    :ok-button-props="{ props: { disabled: isUpdateDisabled } }"
    width="900px"
    @ok="() => handleUpdateInvoice()"
    @cancel="$emit('close')"
  >
    <a-form layout="horizontal">
      <a-form-item
        :label-col="{ span: 8 }"
        :wrapper-col="{ span: 16 }"
        :validate-status="$validator.errors.first('date') ? 'error' : ''"
        :help="$validator.errors.first('date')"
        label="Invoice date"
      >
        <a-date-picker
          v-validate="'required'"
          :value="parseDate(updatedInvoice.date)"
          class="w-100"
          data-vv-name="date"
          @change="(date) => onDatesChange(date, 'date')"
        />
      </a-form-item>

      <a-form-item :label-col="{ span: 8 }" :wrapper-col="{ span: 16 }" label="Status">
        <a-select
          v-if="updatedInvoice.type === 'RequestedInvoice'"
          v-model="updatedInvoice.status"
          placeholder="Select a status"
        >
          <a-select-option value="requested">Requested</a-select-option>
          <a-select-option value="approved">Approved</a-select-option>
          <a-select-option value="rejected">Rejected</a-select-option>
        </a-select>
        <span v-else>{{ updatedInvoice.status || "-" }}</span>
      </a-form-item>

      <a-form-item :label-col="{ span: 8 }" :wrapper-col="{ span: 16 }" label="Comments">
        <a-textarea v-model="updatedInvoice.comment" :auto-size="{ minRows: 3 }" />
      </a-form-item>
    </a-form>

    <div v-if="updatedInvoice.type === 'ReportedInvoice' && usesReportedAmountRows" class="mt-4">
      <div class="d-flex justify-content-between mb-3">
        <h3 class="m-0">Reported invoice rows</h3>
        <a-button type="primary" @click="handleAddManualReportedRow">
          <a-icon type="plus" />Add manual row
        </a-button>
      </div>
      <a-table
        :columns="invoiceReportedItemsEditableTableColumns"
        :data-source="updatedInvoice.reported_invoice_items || []"
        :pagination="false"
        :scroll="{ x: 900 }"
        :row-key="(row) => row.local_key"
      >
        <template #description="description, row">
          <a-input v-model="row.description" />
        </template>
        <template #amountMajor="amountMajor, row">
          <a-input
            :value="getReportedAmountInputValue(row)"
            inputmode="decimal"
            @input="(event) => onReportedAmountInput(row, event?.target?.value)"
            @blur="(event) => commitReportedAmount(row, event?.target?.value)"
            @pressEnter="(event) => commitReportedAmount(row, event?.target?.value)"
          />
        </template>
        <template #amountCurrency="amountCurrency, row">
          <span>{{ row.amount_currency }}</span>
        </template>
        <template #totalCost="totalCost, row">
          {{
            formatCentsWithCurrency(
              parseMajorAmountToCents(row.amount_major) || 0,
              row.amount_currency
            )
          }}
        </template>
        <template #actions="text, row">
          <a-icon
            type="minus-circle"
            class="action-danger"
            @click="handleRemoveReportedRow(row.local_key)"
          />
        </template>
      </a-table>

      <div class="mt-3">
        <div>
          <strong>Selected reports total:</strong>
          {{ formatCentsWithCurrency(selectedTotalCents, selectedCurrency) }}
        </div>
        <div>
          <strong>Invoice rows total:</strong>
          {{ formatCentsWithCurrency(rowsTotalCents, rowsCurrency) }}
        </div>
        <div>
          <strong>Remaining amount:</strong>
          {{ formatCentsWithCurrency(rowsDiffCents, selectedCurrency || rowsCurrency) }}
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, watch, toRef, getCurrentInstance, computed } from "vue";
import useMixin from "@/useMixin";
import useFilters from "@/useFilters";
import { invoiceReportedItemsEditableTableColumns } from "@/modules/invoices/_utils/tables";
import {
  formatCentsWithCurrency,
  normalizeReportedItemForForm,
  normalizeReportedItemsForPayload,
  parseMajorAmountToCents,
  reportedRowsDiffCents,
  reportedRowsTotalCents,
  validateReportedRows as getReportedRowsValidation,
} from "@/modules/invoices/_utils/reported-invoice-items";
import { ReportedInvoiceItemFormInterface } from "@/modules/invoices/types";
import { useReportedInvoiceRows } from "@/modules/invoices/_composables/useReportedInvoiceRows";

// Props
const props = defineProps({
  visible: { type: Boolean, default: false },
  invoice: { type: Object, default: undefined },
});

// Emits
const emits = defineEmits(["update-invoice", "close"]);

// Mixins
const { setObject } = useMixin();

// Filters
const { parseDate } = useFilters();

// Instances
const instance = getCurrentInstance();
const $validator = instance?.proxy.$validator;
const $error = instance?.proxy.$error;

// Data properties
const updatedInvoice = ref<any>({
  date: undefined,
  comment: undefined,
  status: undefined,
  type: undefined,
  report_ids: [],
  reported_invoice_items: [],
});

const reportedInvoiceItems = computed<Array<ReportedInvoiceItemFormInterface>>({
  get: () => updatedInvoice.value.reported_invoice_items || [],
  set: (rows) => {
    updatedInvoice.value.reported_invoice_items = rows;
  },
});

const {
  withReportedRowKey,
  resetReportedAmountDrafts,
  handleAddManualReportedRow,
  onReportedAmountInput,
  getReportedAmountInputValue,
  commitReportedAmount,
  handleRemoveReportedRow,
} = useReportedInvoiceRows({
  rows: reportedInvoiceItems,
  rowKeyPrefix: "reported-edit-row",
  defaultCurrency: computed(
    () => reportedInvoiceItems.value[0]?.amount_currency || selectedCurrency.value || "SEK"
  ),
});

// Watchers
watch(toRef(props, "visible"), (newProp, oldProp) => {
  if (newProp && !oldProp) {
    updatedInvoice.value = setObject(updatedInvoice.value, props.invoice);
    updatedInvoice.value.type = props.invoice?.type || "RequestedInvoice";
    if (updatedInvoice.value.type === "ReportedInvoice") {
      updatedInvoice.value.report_ids = extractReportIds(props.invoice);
      updatedInvoice.value.reported_invoice_items = (
        props.invoice?.reported_invoice_items || []
      ).map((item: any) => withReportedRowKey(normalizeReportedItemForForm(item)));
    }
    resetReportedAmountDrafts();
  }
});

// Component methods
const onDatesChange = (date: any, attribute: string): void => {
  updatedInvoice.value[attribute] = date ? date.format("YYYY-MM-DD") : undefined;
};

const extractReportIds = (invoice: any): Array<number> => {
  if (invoice?.report_ids && invoice.report_ids.length) {
    return invoice.report_ids;
  }
  const roleReportIds = (invoice?.roles || [])
    .flatMap((role: any) => role.reports || [])
    .map((report: any) => report.id);
  const expenseReportIds = (invoice?.expense_reports || []).map((report: any) => report.id);
  return [...new Set([...roleReportIds, ...expenseReportIds])];
};

const validateReportedRows = (): boolean => {
  const rows = updatedInvoice.value.reported_invoice_items || [];
  const validation = getReportedRowsValidation({
    rows,
    selectedReportsCurrency: selectedCurrency.value,
    selectedReportsTotalCents: selectedTotalCents.value,
  });

  if (!validation.hasRows) {
    $error?.({
      title: "Missing invoice rows",
      content: "Please add at least one reported invoice row.",
    });
    return false;
  }

  if (validation.hasInvalidAmount) {
    $error?.({
      title: "Invalid amount",
      content: "Please provide valid row amounts (up to 2 decimals).",
    });
    return false;
  }

  if (validation.hasCurrencyMismatch) {
    $error?.({
      title: "Currency mismatch",
      content: "Reported invoice row currency must match selected reports currency.",
    });
    return false;
  }

  if (validation.hasTotalMismatch) {
    $error?.({
      title: "Invalid row total",
      content: "Reported invoice rows total must match selected reports total.",
    });
    return false;
  }

  return true;
};

const serializeReportedRows = (): Array<any> => {
  return normalizeReportedItemsForPayload(updatedInvoice.value.reported_invoice_items || []);
};

const selectedTotalCents = computed(() => Number(props.invoice?.total_reports_amount?.value) || 0);
const selectedCurrency = computed(
  () => props.invoice?.total_reports_amount?.currency_code || "SEK"
);
const rowsTotalCents = computed(() =>
  reportedRowsTotalCents(updatedInvoice.value.reported_invoice_items || [])
);
const rowsDiffCents = computed(() =>
  reportedRowsDiffCents(selectedTotalCents.value, rowsTotalCents.value)
);
const rowsCurrency = computed(
  () => updatedInvoice.value.reported_invoice_items?.[0]?.amount_currency || null
);
const usesReportedAmountRows = computed(
  () =>
    updatedInvoice.value.type === "ReportedInvoice" &&
    (props.invoice?.reported_invoice_items?.length ?? 0) > 0
);
const isUpdateDisabled = computed(() => {
  if (!usesReportedAmountRows.value) {
    return false;
  }
  const validation = getReportedRowsValidation({
    rows: updatedInvoice.value.reported_invoice_items || [],
    selectedReportsCurrency: selectedCurrency.value,
    selectedReportsTotalCents: selectedTotalCents.value,
  });
  return !validation.isValid;
});

const handleUpdateInvoice = (): void => {
  $validator?.validateAll().then((result) => {
    if (!result) {
      return;
    }

    if (updatedInvoice.value.type === "ReportedInvoice" && usesReportedAmountRows.value) {
      if (!validateReportedRows()) {
        return;
      }
      emits("update-invoice", {
        ...updatedInvoice.value,
        type: "ReportedInvoice",
        report_ids: updatedInvoice.value.report_ids || extractReportIds(updatedInvoice.value),
        reported_invoice_items: serializeReportedRows(),
      });
      emits("close");
      return;
    }

    if (updatedInvoice.value.type === "ReportedInvoice") {
      emits("update-invoice", {
        ...updatedInvoice.value,
        type: "ReportedInvoice",
      });
      emits("close");
      return;
    }

    emits("update-invoice", {
      ...updatedInvoice.value,
      type: "RequestedInvoice",
    });
    emits("close");
  });
};
</script>
